import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { BankAccountAdmin, OrgProfile } from '@foundation/shared';
import { api } from '../lib/api';
import { invalidateBranding } from '../lib/branding';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

/**
 * ตั้งค่าที่เคยต้องแก้ด้วย SQL — ย้ายมาไว้บนหน้าเว็บ
 *
 * เลขบัญชีและรหัสพร้อมเพย์คือค่าที่ **แก้ผิดแล้วเงินไปผิดที่ทันที**
 * หน้านี้จึงเตือนให้เห็นชัด และตรวจรหัสพร้อมเพย์ก่อนบันทึกเสมอ
 */
export default function Settings() {
  const { me, allowed } = useAuth();
  const canWrite = allowed('settings:write');
  const canLinkStorage = me?.role === 'super_admin' || me?.role === 'admin';

  const [banks, setBanks] = useState<BankAccountAdmin[] | null>(null);
  const [org, setOrg] = useState<OrgProfile | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);
  // บันทึกบัญชีแล้วต้องสร้าง QR ใหม่ ไม่งั้นผู้ดูแลจะตรวจ QR ของค่าเก่า
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [b, o] = await Promise.all([
        api<{ data: BankAccountAdmin[] }>('/settings/bank-accounts'),
        api<OrgProfile>('/settings/org'),
      ]);
      setBanks(b.data);
      setOrg(o);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function flash(msg: string) {
    setReloadKey((k) => k + 1);
    setSaved(msg);
    window.setTimeout(() => setSaved(null), 4000);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ตั้งค่าระบบ</h1>
          <p>บัญชีรับบริจาคและข้อมูลมูลนิธิที่ใช้บนใบเสร็จ</p>
        </div>
      </div>

      <ErrorAlert error={error} />
      {saved && <div className="alert success">{saved}</div>}

      {!canWrite && (
        <div className="alert info">
          คุณดูได้อย่างเดียว — การแก้ไขต้องใช้สิทธิ์ผู้ดูแลระบบ
        </div>
      )}

      {banks === null ? (
        <Loading />
      ) : (
        <>
          <div className="alert warn">
            <strong>ระวัง:</strong> รหัสพร้อมเพย์คือปลายทางของเงินบริจาคทุกบาท
            แก้ผิดแล้วเงินจะเข้าบัญชีอื่นทันทีโดยไม่มีอะไรเตือน
            ทุกการแก้ไขถูกบันทึกไว้ว่าใครแก้เมื่อไร
          </div>

          {banks.map((b) => (
            <BankCard
              key={b.id}
              bank={b}
              canWrite={canWrite}
              onSaved={(msg) => {
                flash(msg);
                void load();
              }}
              onError={setError}
            />
          ))}
        </>
      )}

      {org && (
        <OrgCard
          org={org}
          canWrite={canWrite}
          onSaved={(msg) => {
            flash(msg);
            void load();
          }}
          onError={setError}
        />
      )}

      <LogoCard canWrite={canWrite} onFlash={flash} onError={setError} />

      {canWrite && <PromptPayCard reloadKey={reloadKey} />}

      <StorageCard canLink={canLinkStorage} onFlash={flash} onError={setError} />

      {canLinkStorage && <HandoverCard onError={setError} />}
    </>
  );
}

function BankCard({
  bank,
  canWrite,
  onSaved,
  onError,
}: {
  bank: BankAccountAdmin;
  canWrite: boolean;
  onSaved: (msg: string) => void;
  onError: (e: unknown) => void;
}) {
  const [form, setForm] = useState({
    bankCode: bank.bankCode,
    bankName: bank.bankName,
    accountNo: bank.accountNo,
    accountName: bank.accountName,
    promptPayId: bank.promptPayId ?? '',
  });
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const dirty =
    form.bankCode !== bank.bankCode ||
    form.bankName !== bank.bankName ||
    form.accountNo !== bank.accountNo ||
    form.accountName !== bank.accountName ||
    form.promptPayId !== (bank.promptPayId ?? '');

  // เตือนตั้งแต่ยังพิมพ์ ไม่ต้องรอเซิร์ฟเวอร์ตอบ
  const ppDigits = form.promptPayId.replace(/\D/g, '');
  const ppOk = ppDigits === '' || [10, 13, 15].includes(ppDigits.length);

  async function save() {
    setBusy(true);
    try {
      await api(`/settings/bank-accounts/${bank.id}`, { method: 'PATCH', body: form });
      onSaved('บันทึกบัญชีรับบริจาคแล้ว');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        บัญชีรับบริจาค{' '}
        {bank.isDefault && <span className="badge green">บัญชีหลัก</span>}{' '}
        {!bank.isActive && <span className="badge">ปิดใช้งาน</span>}
      </h2>

      <div className="form-row">
        <div className="field">
          <label htmlFor={`bc-${bank.id}`}>รหัสธนาคาร</label>
          <input
            id={`bc-${bank.id}`}
            value={form.bankCode}
            onChange={set('bankCode')}
            disabled={!canWrite}
            placeholder="014"
          />
          <p className="hint">SCB 014 · KBANK 004 · KTB 006 · BBL 002 · TTB 011</p>
        </div>
        <div className="field grow">
          <label htmlFor={`bn-${bank.id}`}>ชื่อธนาคาร</label>
          <input id={`bn-${bank.id}`} value={form.bankName} onChange={set('bankName')} disabled={!canWrite} />
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor={`an-${bank.id}`}>เลขที่บัญชี</label>
          <input
            id={`an-${bank.id}`}
            className="mono"
            value={form.accountNo}
            onChange={set('accountNo')}
            disabled={!canWrite}
            placeholder="123-4-56789-0"
          />
        </div>
        <div className="field grow">
          <label htmlFor={`am-${bank.id}`}>ชื่อบัญชี</label>
          <input
            id={`am-${bank.id}`}
            value={form.accountName}
            onChange={set('accountName')}
            disabled={!canWrite}
            placeholder="มูลนิธิ…"
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor={`pp-${bank.id}`}>รหัสพร้อมเพย์ (ใช้สร้าง QR)</label>
        <input
          id={`pp-${bank.id}`}
          className="mono"
          value={form.promptPayId}
          onChange={set('promptPayId')}
          disabled={!canWrite}
          placeholder="0812345678 หรือ 0123456789012"
        />
        {!ppOk && (
          <p className="alert error">
            ต้องเป็นเบอร์โทร 10 หลัก เลขประจำตัว 13 หลัก หรือ e-wallet 15 หลัก
          </p>
        )}
        <p className="hint">
          {bank.promptPayId
            ? bank.promptPayValid
              ? '✓ ตอนนี้สร้าง QR ได้ปกติ'
              : '⚠ ค่าที่บันทึกไว้ตอนนี้สร้าง QR ไม่ได้'
            : 'ยังไม่ได้ตั้ง — หน้าบริจาคจะไม่แสดง QR จนกว่าจะใส่ค่านี้'}
        </p>
      </div>

      {canWrite && (
        <div className="btn-row">
          <button className="btn" disabled={busy || !dirty || !ppOk} onClick={() => void save()}>
            บันทึก
          </button>
          {dirty && (
            <button
              className="btn ghost"
              disabled={busy}
              onClick={() =>
                setForm({
                  bankCode: bank.bankCode,
                  bankName: bank.bankName,
                  accountNo: bank.accountNo,
                  accountName: bank.accountName,
                  promptPayId: bank.promptPayId ?? '',
                })
              }
            >
              ยกเลิกการแก้ไข
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function OrgCard({
  org,
  canWrite,
  onSaved,
  onError,
}: {
  org: OrgProfile;
  canWrite: boolean;
  onSaved: (msg: string) => void;
  onError: (e: unknown) => void;
}) {
  const [form, setForm] = useState(org);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof OrgProfile) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    setBusy(true);
    try {
      await api('/settings/org', { method: 'PUT', body: form });
      invalidateBranding();
      onSaved('บันทึกข้อมูลมูลนิธิแล้ว');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>ข้อมูลมูลนิธิ (ขึ้นบนหัวใบเสร็จ)</h2>

      <div className="form-row">
        <div className="field grow">
          <label htmlFor="org-name">ชื่อมูลนิธิ</label>
          <input id="org-name" value={form.nameTh} onChange={set('nameTh')} disabled={!canWrite} />
        </div>
        <div className="field">
          <label htmlFor="org-tax">เลขประจำตัวผู้เสียภาษี</label>
          <input
            id="org-tax"
            className="mono"
            value={form.taxId}
            onChange={set('taxId')}
            disabled={!canWrite}
            placeholder="0123456789012"
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="org-addr">ที่อยู่</label>
        <textarea id="org-addr" rows={2} value={form.address} onChange={set('address')} disabled={!canWrite} />
      </div>

      <div className="field">
        <label htmlFor="org-charity">ลำดับที่ในประกาศกระทรวงการคลัง (องค์การสาธารณกุศล)</label>
        <input
          id="org-charity"
          className="mono"
          value={form.charityAnnouncementNo}
          onChange={set('charityAnnouncementNo')}
          disabled={!canWrite}
          placeholder="เช่น 123"
        />
        <p className="hint">
          ถ้ามูลนิธิ<strong>ไม่อยู่ในประกาศนี้ ผู้บริจาคนำไปลดหย่อนภาษีไม่ได้เลย</strong>{' '}
          ต่อให้ออกใบเสร็จครบทุกใบ — ตรวจสถานะกับกรมสรรพากรก่อนกรอก
        </p>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="org-phone">โทรศัพท์</label>
          <input id="org-phone" value={form.phone} onChange={set('phone')} disabled={!canWrite} />
        </div>
        <div className="field grow">
          <label htmlFor="org-footer">ข้อความท้ายใบเสร็จ</label>
          <input
            id="org-footer"
            value={form.receiptFooter}
            onChange={set('receiptFooter')}
            disabled={!canWrite}
          />
        </div>
      </div>

      {canWrite && (
        <div className="btn-row">
          <button className="btn" disabled={busy || !form.nameTh.trim()} onClick={() => void save()}>
            บันทึก
          </button>
        </div>
      )}
    </div>
  );
}

interface StorageStatus {
  provider: 'gdrive' | 'r2' | null;
  googleDrive: {
    connected: boolean;
    accountEmail?: string;
    connectedAt?: string;
    /** เชื่อมไว้แล้ว "ใช้ได้จริงไหม" — ต่างจาก connected ที่บอกแค่ว่ามีค่าเก็บไว้ */
    healthy?: boolean;
    healthError?: string;
  };
  r2Bound: boolean;
  googleOAuthReady: boolean;
}

/**
 * เชื่อม Google Drive ของมูลนิธิเป็นที่เก็บรูปและเอกสาร
 *
 * ใช้บัญชีเดียวของมูลนิธิ ไม่ใช่ของเจ้าหน้าที่แต่ละคน — เจ้าหน้าที่ลาออกแล้ว
 * ไฟล์ต้องยังอยู่ และคนอ่านไฟล์ได้ยังต้องเป็นไปตามสิทธิ์ในระบบเหมือนเดิม
 * (ไฟล์ไม่ได้แชร์ลิงก์สาธารณะ ทุกครั้งที่เปิดยังผ่านการตรวจสิทธิ์ของ Worker)
 */
function StorageCard({
  canLink,
  onFlash,
  onError,
}: {
  canLink: boolean;
  onFlash: (msg: string) => void;
  onError: (e: unknown) => void;
}) {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [busy, setBusy] = useState<'connect' | 'disconnect' | 'test' | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [returned, setReturned] = useState<{ kind: string; detail: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api<StorageStatus>('/settings/storage'));
    } catch (e) {
      onError(e);
    }
  }, [onError]);

  // Google ส่งกลับมาที่ /settings?storage=... — อ่านผลแล้วล้าง query ทิ้ง
  // ไม่งั้นกด refresh ทีไรก็ขึ้นข้อความเดิมทั้งที่ไม่ได้เพิ่งเชื่อม
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const kind = q.get('storage');
    if (!kind) return;
    setReturned({ kind, detail: q.get('detail') ?? '' });
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function connect() {
    setBusy('connect');
    try {
      const { authorizeUrl } = await api<{ authorizeUrl: string }>(
        '/settings/storage/google/authorize',
        { method: 'POST' },
      );
      window.location.href = authorizeUrl;
    } catch (e) {
      onError(e);
      setBusy(null);
    }
  }

  async function disconnect() {
    if (
      !window.confirm(
        'เลิกเชื่อม Google Drive?\n\nไฟล์เดิมยังอยู่ใน Drive ของมูลนิธิ ไม่ถูกลบ ' +
          'แต่ระบบจะอ่านไฟล์เก่าและอัปโหลดไฟล์ใหม่ไม่ได้จนกว่าจะเชื่อมใหม่',
      )
    ) {
      return;
    }
    setBusy('disconnect');
    try {
      await api('/settings/storage/google/disconnect', { method: 'POST' });
      onFlash('เลิกเชื่อม Google Drive แล้ว (ไฟล์เดิมยังอยู่)');
      await load();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setBusy('test');
    setTestResult(null);
    try {
      const r = await api<{ ok: boolean; provider: string }>('/settings/storage/test', {
        method: 'POST',
      });
      setTestResult(`✓ เขียนและอ่านไฟล์ทดสอบผ่าน (${PROVIDER_LABEL[r.provider] ?? r.provider})`);
    } catch (e) {
      setTestResult(`✗ ${(e as { message?: string }).message ?? 'ทดสอบไม่ผ่าน'}`);
    } finally {
      setBusy(null);
    }
  }

  if (!status) return null;

  const gd = status.googleDrive;

  return (
    <div className="card">
      <h2>ที่เก็บไฟล์ (รูปลงพื้นที่ · สลิป · เอกสาร)</h2>

      {returned?.kind === 'connected' && (
        <div className="alert success">เชื่อม Google Drive สำเร็จ — บัญชี {returned.detail}</div>
      )}
      {returned?.kind === 'denied' && (
        <div className="alert warn">คุณยกเลิกการให้สิทธิ์ที่หน้า Google จึงยังไม่ได้เชื่อม</div>
      )}
      {returned?.kind === 'error' && (
        <div className="alert error">เชื่อมไม่สำเร็จ: {returned.detail}</div>
      )}

      <p className="hint">
        ตอนนี้ใช้:{' '}
        <strong>{status.provider ? PROVIDER_LABEL[status.provider] : 'ยังไม่ได้ตั้งค่า'}</strong>
        {status.provider === 'gdrive' && status.r2Bound && ' (Drive มาก่อน R2)'}
      </p>

      {!status.provider && (
        <div className="alert error">
          ยังไม่มีที่เก็บไฟล์ — อัปโหลดรูปลงพื้นที่และสลิปจะใช้ไม่ได้ทั้งระบบ
        </div>
      )}

      {gd.connected ? (
        <>
          {gd.healthy === false && (
            <div className="alert error">
              <strong>เชื่อมไว้แล้วแต่ตอนนี้ใช้งานไม่ได้</strong> — อัปโหลดรูปและสลิปจะพังทันที
              <br />
              {gd.healthError}
              <br />
              กด “เชื่อม Google Drive” อีกครั้งด้วยบัญชีเดิม ไฟล์เก่าจะกลับมาเปิดได้ตามปกติ
            </div>
          )}

          <div className="field">
            <label>
              บัญชี Google ที่เชื่อมไว้{' '}
              {gd.healthy === true && <span className="badge green">ใช้งานได้</span>}
              {gd.healthy === false && <span className="badge">ใช้งานไม่ได้</span>}
            </label>
            <p className="mono">{gd.accountEmail}</p>
            <p className="hint">
              เชื่อมเมื่อ {gd.connectedAt ? new Date(gd.connectedAt).toLocaleString('th-TH') : '—'} ·
              ไฟล์ทั้งหมดอยู่ในโฟลเดอร์ “ระบบมูลนิธิ — ไฟล์แนบ” ของบัญชีนี้
            </p>
          </div>
          {canLink && (
            <div className="btn-row">
              <button className="btn ghost" disabled={busy !== null} onClick={() => void test()}>
                ทดสอบอัปโหลด
              </button>
              <button className="btn danger" disabled={busy !== null} onClick={() => void disconnect()}>
                เลิกเชื่อม
              </button>
              <button
                className={gd.healthy === false ? 'btn' : 'btn ghost'}
                disabled={busy !== null}
                onClick={() => void connect()}
              >
                {gd.healthy === false ? 'เชื่อมใหม่' : 'เปลี่ยนบัญชี'}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p>
            เชื่อมบัญชี Google ของมูลนิธิหนึ่งบัญชี แล้วไฟล์แนบทั้งหมดจะถูกเก็บใน Drive ของบัญชีนั้น
            ระบบขอสิทธิ์เฉพาะไฟล์ที่ตัวเองสร้าง (<span className="mono">drive.file</span>) จึงอ่านไฟล์อื่น
            ใน Drive ไม่ได้
          </p>
          {!status.googleOAuthReady && (
            <div className="alert warn">
              ยังตั้งค่า <span className="mono">GOOGLE_CLIENT_ID</span> /{' '}
              <span className="mono">GOOGLE_CLIENT_SECRET</span> ไม่ครบ — ผู้ดูแลระบบต้องใส่ค่าเหล่านี้
              ก่อนจึงจะกดเชื่อมได้
            </div>
          )}
          {canLink && (
            <div className="btn-row">
              <button
                className="btn"
                disabled={busy !== null || !status.googleOAuthReady}
                onClick={() => void connect()}
              >
                เชื่อม Google Drive
              </button>
              {status.r2Bound && (
                <button className="btn ghost" disabled={busy !== null} onClick={() => void test()}>
                  ทดสอบอัปโหลด
                </button>
              )}
            </div>
          )}
        </>
      )}

      {!canLink && <p className="hint">เชื่อมหรือเลิกเชื่อมได้เฉพาะผู้ดูแลระบบ</p>}
      {testResult && (
        <div className={`alert ${testResult.startsWith('✓') ? 'success' : 'error'}`}>{testResult}</div>
      )}
    </div>
  );
}

const PROVIDER_LABEL: Record<string, string> = {
  gdrive: 'Google Drive ของมูลนิธิ',
  r2: 'Cloudflare R2',
};

/**
 * ถอนตัวเองออกจากระบบหลังส่งมอบ
 *
 * คนที่วางระบบให้มูลนิธิมักเป็นคนนอก และสิทธิ์ super_admin ของเขามัก "ค้าง"
 * อยู่ตลอดไปเพราะไม่มีใครนึกถึงตอนส่งมอบ ปุ่มนี้ทำให้เขาถอนตัวเองได้
 * โดยไม่ต้องรอให้ใครมากดให้
 *
 * ระบบไม่ยอมให้ super_admin คนสุดท้ายถอนตัว จึงไม่มีทางกดจนไม่เหลือใครดูแล
 */
function HandoverCard({ onError }: { onError: (e: unknown) => void }) {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  if (!me) return null;
  const matches = confirm.trim() === me.staffCode;

  async function leave() {
    if (!me) return;
    setBusy(true);
    try {
      await api('/staff/me/leave', {
        method: 'POST',
        body: { confirmStaffCode: confirm.trim(), reason: 'ถอนตัวเองหลังส่งมอบ' },
      });
      // ถอนแล้วเข้าไม่ได้อีก — เคลียร์ session ฝั่งเว็บให้ตรงกับความจริงทันที
      await logout();
    } catch (e) {
      onError(e);
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>ส่งมอบระบบ</h2>
      <p className="hint">
        เมื่อส่งมอบให้มูลนิธิเรียบร้อยแล้ว ให้ถอนสิทธิ์ของตัวเองออก
        คนนอกไม่ควรเหลือสิทธิ์ผู้ดูแลระบบค้างไว้กับข้อมูลผู้รับความช่วยเหลือ
      </p>

      <ol className="hint" style={{ paddingLeft: 20 }}>
        <li>เพิ่มผู้ดูแลของมูลนิธิที่หน้า “จัดการเจ้าหน้าที่” พร้อมกรอกอีเมล Google ของเขา</li>
        <li>ให้เขาลองเข้าสู่ระบบจริงหนึ่งครั้ง — ยืนยันว่าเข้าได้ก่อน</li>
        <li>ค่อยกดปุ่มด้านล่างนี้</li>
      </ol>

      {!open ? (
        <div className="btn-row">
          <button className="btn secondary" onClick={() => setOpen(true)}>
            ถอนสิทธิ์ของฉันออกจากระบบ
          </button>
        </div>
      ) : (
        <>
          <div className="alert warn">
            คุณกำลังจะถอนสิทธิ์ของ <strong>{me.fullName}</strong> ({me.staffCode}) —
            หลังจากนี้จะเข้าระบบไม่ได้อีก และต้องให้ผู้ดูแลคนอื่นเพิ่มกลับเท่านั้น
            <br />
            ประวัติการอนุมัติและการจ่ายเงินที่คุณเคยทำยังอยู่ครบ ไม่ถูกลบ
          </div>

          <div className="field">
            <label htmlFor="confirm-leave">
              พิมพ์รหัสเจ้าหน้าที่ของคุณ (<span className="mono">{me.staffCode}</span>) เพื่อยืนยัน
            </label>
            <input
              id="confirm-leave"
              className="mono"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="btn-row">
            <button className="btn danger" disabled={!matches || busy} onClick={() => void leave()}>
              {busy ? 'กำลังถอนสิทธิ์…' : 'ยืนยันถอนสิทธิ์'}
            </button>
            <button
              className="btn ghost"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setConfirm('');
              }}
            >
              ยกเลิก
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface PromptPayPreview {
  bank: BankAccountAdmin;
  payload: string | null;
  reason?: string;
  amountSatang?: number | null;
  decoded?: {
    idType: 'phone' | 'national_id' | 'ewallet' | 'unknown';
    readable: string;
    amountBaht: number | null;
    crcOk: boolean;
  };
  checks?: { crcOk: boolean; idMatchesSetting: boolean; amountMatches: boolean };
}

const ID_TYPE_LABEL: Record<string, string> = {
  phone: 'เบอร์โทรศัพท์',
  national_id: 'เลขประจำตัว 13 หลัก',
  ewallet: 'e-wallet 15 หลัก',
  unknown: 'ไม่รู้จัก',
};

const PREVIEW_AMOUNTS: Array<{ label: string; satang: number | null }> = [
  { label: 'ไม่ระบุยอด', satang: null },
  { label: '1 บาท', satang: 100 },
  { label: '100 บาท', satang: 10_000 },
];

/**
 * QR ตัวจริงที่ผู้บริจาคจะเห็น — ไว้ให้ผู้ดูแลตรวจก่อนเปิดรับบริจาค
 *
 * สร้างจาก payload เส้นเดียวกับที่ส่งให้ผู้บริจาค ไม่ใช่สร้างใหม่จากค่าในฟอร์ม
 * ถ้าสร้างใหม่จะกลายเป็นการตรวจ "สิ่งที่ตั้งใจ" ไม่ใช่ "สิ่งที่ผู้บริจาคได้จริง"
 * ซึ่งเป็นคนละเรื่องกันทันทีที่ encoder มีบั๊ก
 */
function PromptPayCard({ reloadKey }: { reloadKey: number }) {
  const [amount, setAmount] = useState<number | null>(100);
  const [data, setData] = useState<PromptPayPreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    setError(null);
    setData(null);
    api<PromptPayPreview>(`/settings/promptpay-preview?amount=${amount ?? 0}`)
      .then(setData)
      .catch(setError);
  }, [amount, reloadKey]);

  useEffect(() => {
    if (!data?.payload || !canvasRef.current) return;
    void QRCode.toCanvas(canvasRef.current, data.payload, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'M',
    });
  }, [data]);

  const checks = data?.checks;
  const allOk = checks ? checks.crcOk && checks.idMatchesSetting && checks.amountMatches : false;

  return (
    <div className="card">
      <h2>ตรวจ QR พร้อมเพย์ก่อนเปิดรับบริจาค</h2>
      <ErrorAlert error={error} />

      <div className="btn-row">
        {PREVIEW_AMOUNTS.map((a) => (
          <button
            key={a.label}
            className={amount === a.satang ? 'btn' : 'btn ghost'}
            onClick={() => setAmount(a.satang)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="muted small">กำลังโหลด…</p>
      ) : !data.payload ? (
        <div className="alert warn">{data.reason ?? 'สร้าง QR ไม่ได้'}</div>
      ) : (
        <>
          <div className="form-row" style={{ alignItems: 'flex-start' }}>
            <div className="field">
              <canvas ref={canvasRef} style={{ background: '#fff', padding: 8, borderRadius: 8 }} />
            </div>
            <div className="field grow">
              <dl className="detail-list">
                <dt>เข้าบัญชีชื่อ</dt>
                <dd>{data.bank.accountName}</dd>
                <dt>ธนาคาร</dt>
                <dd>{data.bank.bankName}</dd>
                <dt>รหัสที่ฝังอยู่ใน QR</dt>
                <dd className="mono">
                  {data.decoded?.readable}{' '}
                  <span className="muted">({ID_TYPE_LABEL[data.decoded?.idType ?? 'unknown']})</span>
                </dd>
                <dt>ยอดใน QR</dt>
                <dd>
                  {data.decoded?.amountBaht
                    ? `${data.decoded.amountBaht.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`
                    : 'ให้ผู้โอนใส่เอง'}
                </dd>
              </dl>
            </div>
          </div>

          <div className={`alert ${allOk ? 'success' : 'error'}`}>
            {allOk ? (
              <>✓ ถอด QR กลับมาแล้วตรงกับค่าที่ตั้งไว้ทุกข้อ (รหัส · ยอด · checksum)</>
            ) : (
              <>
                ✗ QR ที่สร้างได้ไม่ตรงกับค่าที่ตั้งไว้ — <strong>อย่าเพิ่งเปิดรับบริจาค</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {checks && !checks.crcOk && <li>checksum ไม่ถูกต้อง แอปธนาคารจะอ่านไม่ผ่าน</li>}
                  {checks && !checks.idMatchesSetting && (
                    <li>รหัสใน QR ไม่ตรงกับรหัสพร้อมเพย์ที่กรอกไว้</li>
                  )}
                  {checks && !checks.amountMatches && <li>ยอดใน QR ไม่ตรงกับยอดที่ขอ</li>}
                </ul>
              </>
            )}
          </div>

          <div className="alert warn">
            <strong>การตรวจที่เชื่อถือได้ที่สุดคือสแกนด้วยแอปธนาคารจริง</strong> แล้วดูว่าขึ้น
            ชื่อบัญชีของมูลนิธิ — การตรวจฝั่งระบบพิสูจน์ได้แค่ว่า QR ตรงกับ “รหัสที่กรอกไว้”
            เท่านั้น ถ้ากรอกรหัสของคนอื่นมาตั้งแต่แรก ทุกอย่างจะขึ้นเขียวหมด
            แต่เงินบริจาคจะเข้าบัญชีคนนั้น
          </div>
        </>
      )}
    </div>
  );
}

const LOGO_MAX_EDGE = 400;
const LOGO_MAX_BYTES = 150 * 1024;

/**
 * ย่อรูปในเบราว์เซอร์ก่อนส่ง
 *
 * คนที่ดูแลมูลนิธิมักมีแต่ไฟล์โลโก้ความละเอียดสูงจากโรงพิมพ์ ถ้าเด้งกลับว่า
 * "ไฟล์ใหญ่เกินไป" เขาจะไม่รู้ว่าต้องย่อยังไงและงานก็จบแค่นั้น — ย่อให้เลยดีกว่า
 */
async function toResizedDataUri(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, LOGO_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('เบราว์เซอร์นี้ย่อรูปไม่ได้');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  // PNG รักษาพื้นหลังโปร่งใสของโลโก้ไว้ ซึ่งสำคัญกับใบเสร็จ
  // แต่ถ้าใหญ่เกินก็ต้องยอมถอยไป JPEG ที่พื้นหลังทึบ
  const png = canvas.toDataURL('image/png');
  if (png.length * 0.75 <= LOGO_MAX_BYTES) return png;
  return canvas.toDataURL('image/jpeg', 0.85);
}

/** โลโก้ที่ขึ้นหัวใบอนุโมทนาบัตร */
function LogoCard({
  canWrite,
  onFlash,
  onError,
}: {
  canWrite: boolean;
  onFlash: (msg: string) => void;
  onError: (e: unknown) => void;
}) {
  const [dataUri, setDataUri] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api<{ dataUri: string | null }>('/settings/logo')
      .then((r) => setDataUri(r.dataUri))
      .catch(onError);
  }, [onError]);

  async function pick(file: File) {
    setBusy(true);
    try {
      const uri = await toResizedDataUri(file);
      const saved = await api<{ dataUri: string | null }>('/settings/logo', {
        method: 'PUT',
        body: { dataUri: uri },
      });
      setDataUri(saved.dataUri);
      invalidateBranding();
      onFlash('บันทึกโลโก้แล้ว — ใบอนุโมทนาบัตรที่พิมพ์หลังจากนี้จะมีโลโก้');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function remove() {
    if (!window.confirm('เอาโลโก้ออกจากใบอนุโมทนาบัตร?')) return;
    setBusy(true);
    try {
      await api('/settings/logo', { method: 'PUT', body: { dataUri: '' } });
      setDataUri(null);
      invalidateBranding();
      onFlash('เอาโลโก้ออกแล้ว');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>โลโก้มูลนิธิ</h2>
      <p className="hint">
        ขึ้นบนหัวใบอนุโมทนาบัตรทุกใบ · รับ PNG, JPG หรือ WebP · ระบบย่อให้อัตโนมัติ
        <br />
        แนะนำ PNG พื้นหลังโปร่งใส จะดูดีที่สุดบนใบเสร็จ
      </p>

      {dataUri === undefined ? (
        <p className="muted small">กำลังโหลด…</p>
      ) : dataUri ? (
        <div
          style={{
            background: '#fff',
            border: '1px solid var(--c-border)',
            borderRadius: 8,
            padding: 12,
            display: 'inline-block',
            marginBottom: 12,
          }}
        >
          <img src={dataUri} alt="โลโก้มูลนิธิ" style={{ maxHeight: 80, maxWidth: 200 }} />
        </div>
      ) : (
        <div className="alert info">ยังไม่ได้ใส่โลโก้ — ใบเสร็จจะขึ้นแค่ชื่อมูลนิธิ</div>
      )}

      {canWrite && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void pick(f);
            }}
          />
          <div className="btn-row">
            <button className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? 'กำลังบันทึก…' : dataUri ? 'เปลี่ยนโลโก้' : 'เลือกไฟล์โลโก้'}
            </button>
            {dataUri && (
              <button className="btn ghost" disabled={busy} onClick={() => void remove()}>
                เอาโลโก้ออก
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
